import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import shadows from "@mui/material/styles/shadows";
import { SearchIcon } from "@/components/common/icons";
import { GREY } from "@/config/mui/colorPalette";
import { useDisabledServicesIds } from "@/hooks/appointments/useDisabledServicesIds";
import { Provider } from "../providerSection";
import { ServiceOption } from "./serviceOption";
import { ServiceOptionGroup } from "./serviceOptionGroup";
import { Service, AvailableService } from "./types";

type Props = {
  availableServices: AvailableService[];
  services: Service[];
  onSelectService: (serviceMenuItem: AvailableService) => void;
  onClearServices: () => void;
  onRemoveService: (index: number) => void;
  selectedProviders: Provider[];
  disabled: boolean;
};

export const ServicesAutocomplete = ({
  availableServices,
  services,
  onSelectService,
  onClearServices,
  onRemoveService,
  selectedProviders,
  disabled,
}: Props) => {
  const selectedServiceMenuItems = services.map(
    (service) => service.serviceMenuItem
  );

  const disabledServicesIds = useDisabledServicesIds({
    appointmentServices: services,
    availableServices,
    selectedProviders,
  });

  const handleAutocompleteAction = (
    reason: AutocompleteChangeReason,
    details: AutocompleteChangeDetails<AvailableService>
  ) => {
    if (reason === "selectOption") {
      onSelectService(details?.option);

      return;
    }
    if (reason === "removeOption") {
      const appointmentServiceIndex = services.findIndex(
        (service) => service.serviceMenuItem.id === details?.option.id
      );
      if (appointmentServiceIndex !== -1) {
        onRemoveService(appointmentServiceIndex);
      }

      return;
    }
    if (reason === "clear") {
      onClearServices();

      return;
    }
  };

  return (
    <>
      <Typography variant="labelTiny" color="text.secondary">
        Service(s)
      </Typography>
      <Autocomplete
        multiple
        value={selectedServiceMenuItems}
        disabled={disabled}
        disablePortal
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        popupIcon={null}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search for services..."
            variant="outlined"
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: <SearchIcon color={GREY[50]} />,
                size: "small",
                sx: {
                  backgroundColor: (theme) =>
                    params.disabled ? GREY[15] : theme.palette.background.paper,
                  pl: "12px !important",
                  py: "8px !important",
                },
              },
            }}
          />
        )}
        slotProps={{
          paper: {
            sx: { boxShadow: shadows[4], borderRadius: 2, mt: 1 },
          },
        }}
        onChange={(_, __, reason, details) =>
          handleAutocompleteAction(reason, details)
        }
        options={availableServices}
        getOptionDisabled={(option) => disabledServicesIds.has(option.id)}
        renderOption={(props, option) => (
          <ServiceOption key={option.id} {...props} service={option} />
        )}
        groupBy={(option) => option.oldCategory?.name ?? "Other"}
        renderGroup={({ group, key, children }) => (
          <ServiceOptionGroup key={key} group={group}>
            {children}
          </ServiceOptionGroup>
        )}
      />
    </>
  );
};
